import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { PlusIcon } from 'assets/icons';
import { Table } from 'components/Table/Table';
import Flyout, { useFlyout } from 'components/Flyout/Flyout';
import { GetFlightsDocument } from 'graphql/generated';
import { useSession } from 'contexts';
import { formatApiDateUTC, removeK } from 'utils/formatter';
import { EllipsisVerticalIcon, MagnifyingGlassIcon, GlobeAmericasIcon, Cog6ToothIcon } from '@heroicons/react/24/solid';
import New from './New';
import EditFlight from './Edit';
import useParamState from 'hooks/useParamState';
import Tabs from 'components/Tabs/Tabs';
import FavoriteRoutes from './FavoriteRoutes';
import NewFavoriteRoute from './FavoriteRoutes/New';
import { Menu, Transition } from '@headlessui/react';
import isMobileApp from 'utils/isMobileApp';
import BulkImport from './Components/BulkImport';
import sanitizeQuery from 'utils/querySanitizer';
import Header from 'components/Header/Header';
import CogMenu from 'components/CogMenu/CogMenu';
import SimpleSearch from 'components/SimpleSearch/SimpleSearch';
import ThumbButton from 'components/ThumbButton/ThumbButton';

const Flights = () => {
  const { flyoutProps, closeFlyout, updateFlyout } = useFlyout();
  const { organizationId } = useSession();
  const [activeTab, setActiveTab] = useParamState('requestsTab', 'Flights');
  const [ limit, setLimit ] = useState(10);
  const [ skip, setSkip ] = useState(0);
  const [ sortBy, setSortBy ] = useState(['flightDate', 'desc']);
  const [ showImport, setShowImport ] = useState(false);
  const [ query, setQuery ] = useState(''); 
  const {
    error,
    data: {
      flights,
      flightCount,
      pendingFlights,
      pendingFlightCount
    } = {},
    refetch
  } = useQuery(GetFlightsDocument, {
    variables: {
      where: {
        craft: {
          is: {
            organizationId: {
              equals: organizationId
            }
          }
        },
        isPending: {
          equals: false
        },
      },
      skip,
      limit,
      orderBy: [{
        [sortBy[0]]: {
          sort: sortBy[1]
        }
      }]
    }
  });
  
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const sanitizedQuery = sanitizeQuery(query);
      const searchData = {
        where: { ...(sanitizedQuery && { OR: [
          { notes: { startsWith: sanitizedQuery } },
          { purpose: { is: { name: { startsWith: sanitizedQuery} } } },
          { icaos: { equals: sanitizedQuery.toUpperCase() } },
          { icaos: { equals: "K"+sanitizedQuery.toUpperCase() } },
          { craft: { is: { name: { startsWith: sanitizedQuery } } } },
          { userProfile: { is:{ user: { is: { firstName: { search: sanitizedQuery.trim().replaceAll(' ', ' | ') } } } } } },
          { userProfile: { is:{ user: { is: { lastName: { search: sanitizedQuery.trim().replaceAll(' ', ' | ') } } } } } },
        ], }),
        isPending: { equals: false },
        craft: { is: { organizationId: { equals: organizationId } } }
       },
      };
      refetch(searchData); 
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [query, refetch]);

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        className: 'justify-center text-left text-xs sm:text-sm w-1/3 sm:w-1/12 min-w-24',
        accessor: 'flightDate',
        Cell: ({ value }: any) => {
          return (
            <div className="text-left">
              <span>{formatApiDateUTC(value)}</span>
            </div>
          );
        },
      },
      {
        Header: 'Route',
        id: 'Route',
        accessor: 'icaos',
        className: 'justify-center text-left text-xs sm:text-sm w-1/3 sm:w-1/12 min-w-0 sm:min-w-28',
        Cell: ({ row, value }: any) => {
          return value?.length ? `${removeK(value?.[0])} - ${removeK(value?.at(-1))}` : 'Unknown';
        }
      },
      {
        Header: 'Pilot',
        accessor: (row: any) => `${row?.pilotName}`,
        className: 'text-left w-1/3 text-xs sm:text-sm sm:w-2/12',
      },
      {
        Header: 'Purpose',
        accessor: (row: any) => row?.purposeName,
        className: 'justify-center text-left w-2/12 min-w-28',
        Cell: ({ row, value }: any) => {
          return (
            <div className="h-full flex items-center">
              <span>{value ?? 'Unknown'}</span>
            </div>
          );
        }
      },
      {
        Header: 'Aircraft',
        accessor: (row: any) => `${row.craft.name}`,
        className: 'text-left w-2/12 ',
        Cell: ({ row, value }: any) => {
          return (
            <div className="h-full flex items-center">
              <span>{value ?? 'Unknown'}</span>
            </div>
          );
        }
      },
      {
        Header: 'Flight Time',
        accessor: (row: any) => ({
          flightTime: row.flightLog,
        }),
        className: 'text-left w-2/12',
        Cell: ({ row, value }) => {
          return (
          <div className="w-full text-left">
            <span>{value?.flightTime}</span>
          </div>
          );
        },
      },
      {
        Header: 'Notes',
        accessor: 'notes',
        className: 'text-center w-2/12 max-w-80',
        Cell: ({ row, value }) => {
          return (
          <div className="truncate overflow-hidden text-ellipsis whitespace-nowrap w-full">
            <span>{value ?? ''}</span>
          </div>
          );
        },
      },
    ],
    []
  );
  const tabs = pendingFlightCount === 0 ? ['Flights', 'Routes'] :  [ 'Flights', {title: 'Pending', count: pendingFlightCount }, 'Routes' ];
  const menuItems = [
    { 
      mobileAction: async () => { await navigator.share({ title:"Export Flights from Coflyt", url: process.env.REACT_APP_API_URL+"/flights/"+organizationId , text: "Export Flights from Coflyt"}) },
      webAction: process.env.REACT_APP_API_URL+"/flights/"+organizationId,
      name: 'Export',
      webOnly: false
    },
    { 
      webAction: () => setShowImport(true),
      name: 'Bulk Import',
      webOnly: true
    }
  ]
  
  if (error) return <p>Error :(</p>;
  return (
    <>
      { showImport && <BulkImport cancel={setShowImport} completed={()=> refetch()} /> }
      <Flyout {...flyoutProps} />
      <div className="">
        {/* Title */}
        <Header title="Flights" icon={GlobeAmericasIcon}>
          <div className="flex justify-end gap-2 flex-wrap order-2 lg:order-3">
            <div className="flex items-stretch gap-2">
              { activeTab === "Flights" && 
                <>
                  <button
                    onClick={()=>updateFlyout({ title: 'New Flight', content: <New closeFlyout={()=>{closeFlyout();}} /> })}
                    className="hidden md:flex items-center bg-brand text-white border border-brand text-sm rounded font-semibold py-1.5 px-4"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" /> 
                    Add Flight
                  </button> 
                  <ThumbButton size="primary" role="add" onClick={()=>updateFlyout({ title: 'New Flight', content: <New closeFlyout={()=>{closeFlyout();}} /> })}/>
                </>
              }
              { activeTab === "Routes" && 
                <>
                  <button
                    onClick={()=> updateFlyout({ title: 'New Favorite Route', content: <NewFavoriteRoute closeFlyout={()=>{closeFlyout();}} /> })}
                    className="hidden md:flex items-center bg-brand text-white border border-brand text-sm rounded font-semibold py-1.5 px-4"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Route
                  </button> 
                  <ThumbButton size="primary" role="add" onClick={()=> updateFlyout({ title: 'New Favorite Route', content: <NewFavoriteRoute closeFlyout={()=>{closeFlyout();}} /> })}/>
                </>
              }
              <CogMenu items={menuItems} />
            </div>
          </div>
          <SimpleSearch query={query} setQuery={setQuery} className="mt-2 pt-2 border-t border-slate-200 w-full order-3 lg:order-2 lg:mt-0 lg:mr-2 lg:pt-0 lg:border-t-0 lg:w-auto" />
        </Header>
        <div className="bg-white border border-slate-300 rounded">
          <div className="flex justify-between items-center border-b border-slate-200 pt-1 bg-slate-50 w-full px-4 rounded-t">
            <Tabs setActiveItem={setActiveTab} activeItem={activeTab} items={tabs} />
          </div> 
          <div className="p-2 md:p-6 w-full">
            { activeTab !== 'Routes' && 
              <>
                {(flights?.length ===0 && activeTab === "Flights" ) ? 
                  <div className='flex flex-col justify-center items-center gap-7'>
                    <div className="text-center text-lg text-slate-400 pt-10">No Flights</div>
                    <button                    
                      onClick={()=>updateFlyout({ title: 'New Flight', content: <New closeFlyout={()=>{closeFlyout();}} /> })}
                      className="flex items-center justify-center border border-brand text-sm rounded font-semibold py-1.5 px-4 w-48">
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add First Flight
                    </button>
                  </div>
                  :
                  
                  <Table
                    className='w-full'
                    skip={skip} limit={limit} count={activeTab === 'Pending' ? pendingFlightCount : flightCount}
                    updateLimitAndSkip = {(lm, sk) => { setLimit(lm); setSkip(sk); }}
                    setSortBy={(sort) => setSortBy(sort)}
                    columns={columns as any}
                    mobileRowCount={3}
                    data={( activeTab === 'Pending' ? 
                      pendingFlights ?? [] : 
                      [...(flights ?? [])] )}
                    onRowClick={(row: any) => updateFlyout({ title: activeTab === 'Pending' ? 'Submit Flight' : 'Edit Flight', content: <EditFlight flightId={row.id} closeFlyout={()=>{closeFlyout();}} /> })}
                    />
                }
              </>
            }
            { activeTab === 'Routes' && 
              <FavoriteRoutes/>
            }
          </div>
        </div>
      </div>
    </>
  );
};
export default Flights;